import { apiFetch, apiUploadFormData } from "@/lib/api";
import { hubApiUrl } from "@/lib/hubApiOrigin";
import { downloadPdfBase64 } from "@/lib/miscPrintingApi";

export type ChangeNoticeStatus =
  | "draft"
  | "issued"
  | "acknowledged"
  | "implemented"
  | "verified"
  | "cancelled";

export interface EngineeringChangeNotice {
  id: string;
  org_id: number;
  number: string;
  title: string;
  description: string | null;
  change_order_id: string | null;
  status: ChangeNoticeStatus;
  supplier_data_id: string | null;
  supplier_name: string | null;
  required_document_action: string | null;
  external_reference: string | null;
  issued_at: string | null;
  acknowledged_at: string | null;
  implemented_at: string | null;
  effective_date: string | null;
  implementation_checked_at: string | null;
  implementation_checked_by: string | null;
  implementation_checked_by_display: string | null;
  verification_result: "pending" | "confirmed" | "failed" | null;
  notes: string | null;
  cm_response_original_filename: string | null;
  cm_response_uploaded_at: string | null;
  cm_response_download_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface ChangeOrderDetail {
  changeOrder: {
    id: string;
    number: string;
    title: string;
    description: string | null;
    implementation_plan: string | null;
    change_reason: string | null;
    status: string;
    owner_user_id: string | null;
    owner_display: string | null;
    enforcement_rule: string | null;
    requested_effective_date: string | null;
    verification_due_date: string | null;
    notes: string | null;
    document_count: string;
    created_at: string;
    updated_at: string;
  };
  productCollections: { product_collection_id: string; code: string; name: string }[];
  skus: { sku: string; product_name: string | null }[];
  changeNotices: EngineeringChangeNotice[];
  events: {
    id: string;
    event_type: string;
    event_note: string | null;
    field_name: string | null;
    old_value: string | null;
    new_value: string | null;
    created_by_display: string | null;
    created_at: string;
  }[];
}

export function fetchChangeOrderDetail(changeOrderId: string): Promise<ChangeOrderDetail> {
  return apiFetch<ChangeOrderDetail>(`/api/document-change-orders/${changeOrderId}`);
}

export function changeNoticeHtmlPreviewUrl(changeOrderId: string, noticeId: string): string {
  return hubApiUrl(`/api/document-change-orders/${changeOrderId}/change-notices/${noticeId}/html`);
}

export async function downloadChangeNoticePdf(changeOrderId: string, noticeId: string): Promise<void> {
  const payload = await apiFetch<{ pdfBase64: string; filename: string }>(
    `/api/document-change-orders/${changeOrderId}/change-notices/${noticeId}/pdf`,
  );
  downloadPdfBase64(payload.pdfBase64, payload.filename);
}

export function uploadChangeNoticeCmResponse(
  changeOrderId: string,
  noticeId: string,
  file: File,
): Promise<{ ok: true; changeNotices: EngineeringChangeNotice[] }> {
  const formData = new FormData();
  formData.append("file", file);
  return apiUploadFormData<{ ok: true; changeNotices: EngineeringChangeNotice[] }>(
    `/api/document-change-orders/${changeOrderId}/change-notices/${noticeId}/cm-response`,
    formData,
  );
}

export function fetchRelatedChangeOrders(query: {
  sku?: string;
  productCollectionId?: string;
  documentId?: string;
}): Promise<{ changeOrders: ChangeOrderDetail["changeOrder"][] }> {
  return apiFetch<{ changeOrders: ChangeOrderDetail["changeOrder"][] }>(
    "/api/document-change-orders/related",
    { query },
  );
}
